import { formatDate } from '@angular/common';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { forkJoin, Subscription } from 'rxjs';
import { RaciNavigationService } from '../../../../navigation/raci-navigation.service';
import { RaciService } from '../../../raci/services/raci.service';
import { AppStorageService } from '../../../../services/app-storage.service';
import { SelectableSettings } from '@progress/kendo-angular-grid';

/**
 * Effective valuation dates setup component
 */
@Component({
  selector: 'valuation-effective-dates-setup',
  templateUrl: './valuation-effective-dates-setup.component.html',
  styleUrls: ['./valuation-effective-dates-setup.component.css']
})
export class ValuationEffectiveDatesSetupComponent implements OnInit, OnDestroy {

  /**
  * Selected valuation event id
  */
  @Input() selectedValuationEventId: any;

  /**
   * Show the external grid
   */
  public showExternalGrid = false;

  /**
   * Grid SelectableSettings
   */
  public selectableSettings: SelectableSettings = {
    enabled: true,
    mode: 'single'
  };

  /**
  * Indicates whether to display task title information
  */
  @Input() showTitleInfo = false;

  /**
  * Project id of the selected valuation event
  */
  @Input() projectId;

  /**
  * Active task information
  */
  @Input() activeTask: any;

  /**
  * Disable functions flag setter
  */
  @Input() set disableFunctions(value) {
    this.isFunctionsDisabled = value;
  }

  /**
  * Flag indicating whether the form actions should be disabled
  */
  isFunctionsDisabled: boolean;

  /**
  * Project information
  */
  project: any;

  /**
  * Flag indicating whether the valuation event is being displayed
  */
  isValEventDisplayed = false;

  /**
  * Flag indicating whether all valuation event properties are selected
  */
  isAllSelected = false;

  /**
  * Valuation events list
  */
  valuationEvents: any[];

  /**
  * Valuation events properties list
  */
  valEventProperties: any[];

  /**
  * Valuation event
  */
  valuationEvent: any;

  /**
  * Effective start date
  */
  effectiveStartDate: any;

  /**
  * Effective end date
  */
  effectiveEndDate: any;

  /**
   * The subscriptions
   */
  private _subscriptions: Array<Subscription> = new Array<Subscription>();

  /**
   * Constructor
   * @param _raciService RaciService
   * @param _raciNavigationService RaciNavigationService
   */ 
  constructor(private _raciService: RaciService,
    private appStorage: AppStorageService,
    private _raciNavigationService: RaciNavigationService) { }

  /**
   * Inititalizes the component
   */
  ngOnInit() {
    this.project = this.appStorage.getSelectedProject();
    this._subscriptions.push(this.getData().subscribe(data => {
      if (data) {
        this.processValuationEventsData(data[0]);
        this.processValuationEventsPropertiesData(data[1]);
        this._raciService.displayDataLoader(false);
      }
    }));
    this.subscribeToSaveEffectiveDates();
  }

  /**
   * Get all data
   */
  private getData() {
    this._raciService.displayDataLoader(true);
    const valuationEvents = this._raciService.getValuationEvents(this.project ? this.project.Oid : this.projectId);
    const properties = this._raciService.getProjectValuationEventsProperties(this.project ? this.project.Oid : this.projectId);
    return forkJoin([valuationEvents, properties]);
  }

  /**
   * Select all properties
   */
  selectAllPropertiesOfValuationEvent() {
    this.isAllSelected = !this.isAllSelected;
    this.valuationEvent.properties.forEach(property => {
      property.isSelected = this.isAllSelected;
      this.setEffectiveDate(property);
    });
  }

  /**
   * On propery selection
   * @param property Selected property
   */
  onPropertySelect(property: any) {
    property.isSelected = !property.isSelected;
    const notSelectedProperties = this.valuationEvent.properties.find(item => !item.isSelected);

    if (!notSelectedProperties) {
      this.isAllSelected = true;
    } else {
      this.isAllSelected = false;
    }

    this.setEffectiveDate(property);
  }

  /**
   * On change start date input
   */
  changeStartDate(value: any) {
    this.valuationEvent.properties.forEach(prop => {
      if (prop.isSelected) prop.valuationEventEffectiveStartDate = value.target.value;
    });
  }

  /**
   * On change start date input
   */
  changeEndDate(value: any) {
    this.valuationEvent.properties.forEach(prop => {
      if (prop.isSelected) prop.valuationEventEffectiveEndDate = value.target.value;
    });
  }

  /**
 * Process valuation events
 * @param data Properties data
 */
  processValuationEventsData(data: any) {
    this.valuationEvent = this.createValuationEvent(data.find(valEvent => valEvent.oid == this.selectedValuationEventId));
  }

  /**
 * Process properties for valuation events
 * @param data Properties data
 */
  processValuationEventsPropertiesData(data: any) {
    this.valuationEvent.properties = this.filterPropertiesByValuationEvent(this.valuationEvent.oid, data);
    this.convertDates();
  }

  /**
   * Converts effectiveEndDate and effectiveStartDate to dates, so they are accepted in the kendo-datepicker
   */
  convertDates() {
    this.valuationEvent.properties.forEach(property => {
      const effectiveEndDate = property.valuationEventEffectiveEndDate;
      const effectiveStartDate = property.valuationEventEffectiveStartDate;
      property.valuationEventEffectiveEndDate = new Date(effectiveEndDate);
      property.valuationEventEffectiveStartDate = new Date(effectiveStartDate);
    })
  }

  /**
 * Filter properties for valuation event
 * @param eventId Valuation Event id
 * @param data Properties data
 */
  private filterPropertiesByValuationEvent(eventId: any, data: any) {
    this.valEventProperties = [];
    const properties = data.filter(item => item.ValuationId === eventId && item.LinkedPropertyValuationId != null);
    properties.forEach(property => {
      this.valEventProperties.push(this.createProperty(property));
    });
    return this.valEventProperties;
  }

  /**
 * Create new parent node
 * @param parentNode Parent node
 */
  private createValuationEvent(parentNode: any): any {
    return {
      "oid": parentNode?.oid,
      "name": parentNode?.valuationPortfolioName,
      "isSelected": false,
      "isAllSelected": false,
      "properties": []
    };
  }

  /**
 * Create new parent node
 * @param parentNode Parent node
 */
  private createProperty(parentNode: any): any {
    return {
      "oid": parentNode.Oid,
      "isSelected": false,
      "municipally": parentNode.MunicipalManual,
      "address": parentNode.Street1,
      "propertyName": parentNode.buildingName,
      "valuationEventEffectiveStartDate": formatDate(parentNode.ValuationEventEffectiveStartDate, 'yyyy-MM-dd', 'en-US'),
      "valuationEventEffectiveEndDate": formatDate(parentNode.ValuationEventEffectiveEndDate, 'yyyy-MM-dd', 'en-US'),
      "valuationId": parentNode.ValuationId
    };
  }

  /**
   * On destory
   */
  ngOnDestroy(): void {
    this._subscriptions.forEach(subscription => { subscription.unsubscribe() });
  }

  /**
   * Subscribe to save valuation events with properties and effective dates
   */
  subscribeToSaveEffectiveDates() {
    this._subscriptions.push(this._raciNavigationService._onSaveEffectiveDates.subscribe(value => {
      if (value) {
        this.saveProperties();
      }
    }));
  }

  /**
   * Process properties for save
   */
  saveProperties() {
    this._raciService.displayDataLoader(true);
    this._subscriptions.push(this._raciService.saveProjectPortfoliosProperties(this.valuationEvent?.properties).subscribe(data => {
      this._raciService.displayDataLoader(false);
    }));
  }

  /**
   * Sets effective dates for selected property
   * @param property Selected property
   */
  private setEffectiveDate(property: any) {
    if (this.effectiveStartDate) {
      property.valuationEventEffectiveStartDate = this.effectiveStartDate;
    }

    if (this.effectiveEndDate) {
      property.valuationEventEffectiveEndDate = this.effectiveEndDate;
    }
  }

}
